import React, { useCallback, useEffect, useRef } from 'react';

import { fileService } from '@/lib/files/service';

import type { ImageNodeData } from './ImageElement';
import { ImageElement } from './ImageElement';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findImageNode(editor: any, lookupUrl: string): [any, any] | null {
  try {
    const nodesIterator = editor.api.nodes({
      at: [],
      match: (n: any) => n.type === 'img' && n.url === lookupUrl, // eslint-disable-line @typescript-eslint/no-explicit-any
    });
    if (nodesIterator && typeof nodesIterator[Symbol.iterator] === 'function') {
      for (const entry of nodesIterator) {
        return entry as [any, any]; // eslint-disable-line @typescript-eslint/no-explicit-any
      }
    }
  } catch (error) {
    console.error('Error finding image node:', error);
  }
  return null;
}

interface UseImageUploadOptions {
  orgId?: number;
  readOnly?: boolean;
  onShowSizeMenu?: (anchorElement: HTMLElement, plateElement: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any -- Plate element with custom image properties not typed in base TElement
}

interface UseImageUploadReturn {
  editorRef: React.MutableRefObject<any>; // eslint-disable-line @typescript-eslint/no-explicit-any -- Plate editor instance not typed for ref storage
  ImageElementWithHandlers: React.FC<any>; // eslint-disable-line @typescript-eslint/no-explicit-any -- Plate image plugin element with custom upload properties not typed
  handlePaste: (event: React.ClipboardEvent) => void;
  handleDrop: (event: React.DragEvent) => void;
  handleImageButtonClick: () => void;
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

export function useImageUpload(
  options: UseImageUploadOptions
): UseImageUploadReturn {
  const { orgId, readOnly, onShowSizeMenu } = options;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate editor instance not typed for ref storage
  const editorRef = useRef<any>(null);

  const pendingUploadsRef = useRef<Map<string, File>>(new Map());

  const handleImageFileUploadRef = useRef<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate image plugin element with custom upload properties not typed
    ((file: File, existingElement?: any) => Promise<void>) | null
  >(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleRetryUpload = useCallback(
    (node: ImageNodeData) => {
      const file = pendingUploadsRef.current.get(node.url);
      if (file && orgId && handleImageFileUploadRef.current) {
        const editor = editorRef.current;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- node cast for findPath; Plate element object
        const retryPath = editor?.api.findPath(node as any);
        if (editor && retryPath) {
          editor.tf.setNodes(
            { uploadRetryCount: (node.uploadRetryCount ?? 0) + 1 },
            { at: retryPath }
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- node cast to existingElement; same underlying Plate element object
        handleImageFileUploadRef.current(file, node as any);
      }
    },
    [orgId]
  );

  const handleShowSizeMenu = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate element with custom image properties not typed in base TElement
    (anchorEl: HTMLElement, plateElement: any) => {
      onShowSizeMenu?.(anchorEl, plateElement);
    },
    [onShowSizeMenu]
  );

  // Custom ImageElement renderer with retry and size menu handlers wired up
  const ImageElementWithHandlers = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate image plugin element with custom upload properties not typed
    (props: any) =>
      React.createElement(ImageElement, {
        ...props,
        onRetryUpload: handleRetryUpload,
        onShowSizeMenu: handleShowSizeMenu,
      }),
    [handleRetryUpload, handleShowSizeMenu]
  );

  // Handle image file upload — uses editorRef.current to avoid circular dep with plugin config
  const handleImageFileUpload = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate image plugin element with custom upload properties not typed
    async (file: File, existingElement?: any) => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      if (!orgId) {
        console.error('Cannot upload image: orgId is not provided');
        return;
      }

      // Validate file is an image
      if (!file.type.startsWith('image/')) {
        console.error('Not an image file:', file.type);
        return;
      }

      // For retries, reuse the existing node's URL for lookup; for new uploads, create a temp URL
      const lookupUrl = existingElement
        ? existingElement.url
        : URL.createObjectURL(file);
      if (!existingElement) {
        pendingUploadsRef.current.set(lookupUrl, file);
      }

      // Insert placeholder (Plate requires children: [{ text: '' }] on void nodes too)
      const imageNode = {
        type: 'img',
        url: lookupUrl,
        alt: file.name,
        width: 'medium',
        uploading: true,
        uploadProgress: 0,
        children: [{ text: '' }],
      };

      let nodePath: any; // eslint-disable-line @typescript-eslint/no-explicit-any

      if (existingElement) {
        nodePath = editor.api.findPath(existingElement);
        if (nodePath) {
          editor.tf.setNodes(
            { uploading: true, uploadProgress: 0, uploadError: undefined },
            { at: nodePath }
          );
        }
      } else {
        editor.tf.insertNodes(imageNode as any); // eslint-disable-line @typescript-eslint/no-explicit-any
        // Small delay to ensure the inserted node is in the tree
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      try {
        // Upload the file
        const uploadedFile = await fileService.uploadFile(orgId, file, {
          onProgress: progress => {
            // Update progress on the node
            const nodeEntry = findImageNode(editor, lookupUrl);
            if (nodeEntry) {
              const [, path] = nodeEntry;
              editor.tf.setNodes({ uploadProgress: progress }, { at: path });
            }
          },
        });

        // Prefer file_url (inline viewing) over download_url
        const imageUrl = uploadedFile.file_url || uploadedFile.download_url;

        const nodeEntry = findImageNode(editor, lookupUrl);
        if (nodeEntry) {
          const [, path] = nodeEntry;
          editor.tf.setNodes(
            {
              url: imageUrl,
              fileId: uploadedFile.id,
              uploading: false,
              uploadProgress: undefined,
              uploadError: undefined,
            },
            { at: path }
          );
        } else {
          console.error(
            '[useImageUpload] Could not find image node to update after upload'
          );
        }

        // Clean up blob URLs to prevent memory leaks (applies to both new uploads and retries)
        if (lookupUrl.startsWith('blob:')) {
          URL.revokeObjectURL(lookupUrl);
        }
        pendingUploadsRef.current.delete(lookupUrl);
      } catch (error) {
        console.error('Image upload failed:', error);

        // Update node with error state
        const nodeEntry = findImageNode(editor, lookupUrl);
        if (nodeEntry) {
          const [, path] = nodeEntry;
          editor.tf.setNodes(
            {
              uploading: false,
              uploadProgress: undefined,
              uploadError: "We couldn't upload this image. Try again.",
            },
            { at: path }
          );
        }
      }
    },
    // editorRef is a stable ref — access .current inside callback, not in deps
    [orgId]
  );

  // Keep ref updated for use in handleRetryUpload (which is memoized with [orgId])
  useEffect(() => {
    handleImageFileUploadRef.current = handleImageFileUpload;
  }, [handleImageFileUpload]);

  // Revoke pending blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    const uploads = pendingUploadsRef;
    return () => {
      for (const url of uploads.current.keys()) {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      }
      uploads.current.clear();
    };
  }, []);

  // Handle paste events for images
  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      if (readOnly || !orgId) {
        return;
      }

      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            handleImageFileUpload(file);
          }
          return;
        }
      }
    },
    [readOnly, orgId, handleImageFileUpload]
  );

  // Handle drop events for images
  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (readOnly || !orgId) {
        return;
      }

      const files = event.dataTransfer?.files;
      if (!files) {
        return;
      }

      for (const file of files) {
        if (file.type.startsWith('image/')) {
          event.preventDefault();
          handleImageFileUpload(file);
          return;
        }
      }
    },
    [readOnly, orgId, handleImageFileUpload]
  );

  // Handle toolbar image button click
  const handleImageButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle file input change (toolbar image picker)
  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly || !orgId) {
        event.target.value = '';
        return;
      }
      const file = event.target.files?.[0];
      if (file && file.type.startsWith('image/')) {
        handleImageFileUpload(file);
      }
      // Reset input so the same file can be selected again
      event.target.value = '';
    },
    [readOnly, orgId, handleImageFileUpload]
  );

  return {
    editorRef,
    ImageElementWithHandlers,
    handlePaste,
    handleDrop,
    handleImageButtonClick,
    handleFileInputChange,
    fileInputRef,
  };
}
